import { useMemo, useState } from 'react';
import { Copy, Check, Share2 } from 'lucide-react';
import { Button, Card, CardContent, Input } from '@jeraisonne/ui';
import { QrCodeImage } from '@/components/qr-code';
import { useEnsureParentLinkCode, useAcceptParentLink } from '@/lib/parent-link';
import {
  buildParentLinkShareUrl,
  normalizeParentLinkCode,
  PARENT_LINK_CODE_LENGTH,
} from '@/lib/parent-link-code';

type Audience = 'student' | 'parent';

interface Props {
  userId: string;
  /** Adapte les libellés selon que la carte est vue par l'élève ou le parent. */
  audience: Audience;
}

const COPY: Record<
  Audience,
  {
    title: string;
    help: string;
    orPrompt: string;
    shareTitle: string;
    shareText: string;
    placeholder: string;
  }
> = {
  student: {
    title: 'Mon code parent',
    help: 'Partage ce code (ou le QR) avec ton parent pour relier vos comptes — il pourra suivre ta progression.',
    orPrompt: 'Ou entre le code de ton parent :',
    shareTitle: 'Relie ton compte au mien sur Jeraisonne',
    shareText: 'Scanne ou ouvre ce lien pour suivre ma progression sur Jeraisonne :',
    placeholder: 'Code parent',
  },
  parent: {
    title: 'Mon code de liaison',
    help: 'Partage ce code (ou le QR) avec ton enfant pour relier vos comptes et suivre sa progression.',
    orPrompt: 'Ou entre le code de ton enfant :',
    shareTitle: 'Relions nos comptes sur Jeraisonne',
    shareText: 'Scanne ou ouvre ce lien pour relier ton compte au mien sur Jeraisonne :',
    placeholder: 'Code enfant',
  },
};

/**
 * Carte « relier parent ⇄ enfant » : affiche le code permanent du compte, un
 * QR code et un bouton « Copier le lien », et permet de saisir le code de
 * l'autre partie pour activer le lien. Réutilisée côté élève (Contacts) et
 * côté parent (Mes enfants) — seuls les libellés changent (`audience`).
 */
export function ParentLinkCard({ userId, audience }: Props) {
  const { data: code, isLoading } = useEnsureParentLinkCode(userId);
  const accept = useAcceptParentLink();
  const labels = COPY[audience];
  const [copied, setCopied] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const shareUrl = useMemo(
    () => (code ? buildParentLinkShareUrl(window.location.origin, code) : ''),
    [code],
  );

  async function handleCopy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — l'utilisateur peut toujours copier manuellement
    }
  }

  async function handleShare() {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: labels.shareTitle, text: labels.shareText, url: shareUrl });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  }

  function handleAccept(e: React.FormEvent) {
    e.preventDefault();
    const c = inputCode.trim();
    if (!c) return;
    accept.mutate(c, {
      onSuccess: (res) => {
        setSuccess(res.linked_display_name);
        setInputCode('');
        setTimeout(() => setSuccess(null), 5000);
      },
    });
  }

  return (
    <Card className="border-primary-200 bg-primary-50/40">
      <CardContent className="space-y-4 py-4">
        <div>
          <p className="text-ink-900 text-sm font-semibold">{labels.title}</p>
          <p className="text-ink-500 mt-0.5 text-xs">{labels.help}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="border-ink-100 rounded-xl border bg-white p-2">
            {code ? (
              <QrCodeImage value={shareUrl} size={120} />
            ) : (
              <div className="bg-ink-100 h-[120px] w-[120px] animate-pulse rounded" />
            )}
          </div>
          <div className="min-w-[9rem] flex-1 space-y-2">
            <p
              className="font-display text-primary-700 select-all break-all text-xl font-extrabold tracking-widest"
              aria-label="Code de liaison"
            >
              {isLoading ? '…' : code}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopy}
                disabled={!code}
                className="gap-1.5"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copié' : 'Copier le lien'}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleShare}
                disabled={!code}
                className="gap-1.5"
              >
                <Share2 className="h-3.5 w-3.5" />
                Partager
              </Button>
            </div>
          </div>
        </div>

        <div className="border-ink-100 border-t pt-3">
          <p className="text-ink-700 mb-2 text-xs font-semibold">{labels.orPrompt}</p>
          <form onSubmit={handleAccept} className="flex gap-2">
            <Input
              value={inputCode}
              onChange={(e) => setInputCode(normalizeParentLinkCode(e.target.value))}
              placeholder="ABCDEFGH"
              maxLength={PARENT_LINK_CODE_LENGTH}
              className="font-mono uppercase tracking-widest"
              aria-label={labels.placeholder}
              disabled={accept.isPending}
            />
            <Button type="submit" disabled={!inputCode.trim() || accept.isPending} size="sm">
              {accept.isPending ? '…' : <Check className="h-4 w-4" />}
            </Button>
          </form>
          {accept.isError && (
            <p className="text-accent-600 mt-2 text-xs">{(accept.error as Error).message}</p>
          )}
          {success && (
            <p className="mt-2 text-xs font-medium text-emerald-600">
              {success} est maintenant relié à ton compte&nbsp;!
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
